import { FC, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from "mantine-react-table";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { Box, Button } from "@mantine/core";
import { IconDownload } from "@tabler/icons-react";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { toast } from "react-toastify";
import useDownloadPaymentReconciliation from "../../../../hooks/useDownloadPaymentReconciliation";
import { PaymentReconciliationData } from "../../../../services/getPaymentReconciliationData";
import useGetPaymentReconciliationData from "../../../../hooks/useGetPaymentReconciliationData";
import { useMemo } from "react";

const PaymentReconciliation: FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: "transactionDate",
      desc: true,
    },
  ]);

  const useDownloadPaymentReconciliationMutation =
    useDownloadPaymentReconciliation();

  const apiColumnFilters: any = JSON.stringify(
    columnFilters
      .filter((filter) => {
        if (Array.isArray(filter.value)) {
          return filter.value.every(
            (v) => v !== null && v !== undefined && v !== ""
          );
        }
        return (
          filter.value !== null &&
          filter.value !== undefined &&
          filter.value !== ""
        );
      })
      .map((filter) => {
        let column = filter.id;
        let value = filter.value;

        if (column === "UPC ID") {
          column = "upcId";
        }
        return {
          [column]: value,
        };
      })
  );

  const apiSorting: any = JSON.stringify(
    sorting.reduce((acc, sort) => {
      if (sort.id && sort.desc !== undefined) {
        acc[sort.id] = sort.desc;
      }
      return acc;
    }, {} as Record<string, any>)
  );

  const { data, error, isLoading } = useGetPaymentReconciliationData(
    token,
    pagination,
    apiColumnFilters,
    apiSorting
  );

  if (error) {
    toast.error((error as Error).message);
  }

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  const handleDownloadPaymentReconciliation = () => {
    useDownloadPaymentReconciliationMutation.mutate(
      { token, apiColumnFilters, apiSorting },
      {
        onSuccess: (data: Blob) => {
          downloadFile(data, "PaymentReconciliation", FileType.CSV);
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      }
    );
  };

  const columns = useMemo<MRT_ColumnDef<PaymentReconciliationData>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "merchantEmail",
        header: "Merchant Email",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "currency",
        header: "Currency",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "quantitySold",
        header: "Quantity Sold",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
        filterVariant: "range",
      },
      {
        accessorKey: "transactionId",
        header: "Transaction ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "transactionDate",
        header: "Transaction Date",
        filterVariant: "date-range",
        Cell: ({ cell }) => (
          <p>
            {new Date(cell.getValue<string>())
              .toLocaleString("en-US", {
                timeZone: "Asia/Kuala_Lumpur",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              })
              .replace(",", "")}
          </p>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "isPaymentReceived",
        header: "Payment Received",
        Cell: ({ cell }) => (
          <span>{cell.getValue<boolean>() ? "Yes" : "No"}</span>
        ),
      },
      {
        accessorKey: "isInventorySent",
        header: "Inventory Sent",
        Cell: ({ cell }) => (
          <span>{cell.getValue<boolean>() ? "Yes" : "No"}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created Date",
        filterVariant: "date-range",
      },
      {
        accessorKey: "updatedAt",
        header: "Updated Date",
        filterVariant: "date-range",
      },
      {
        accessorKey: "upcId",
        header: "UPC ID",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
      {
        accessorKey: "amountToTransferFromMerchant",
        header: "Amount To Transfer",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
        filterVariant: "range",
      },
      {
        accessorKey: "isNoteIssued",
        header: "Note Issued",
        Cell: ({ cell }) => (
          <span>{cell.getValue<boolean>() ? "Yes" : "No"}</span>
        ),
      },
      {
        accessorKey: "upcUnitSellingPrice",
        header: "Unit Selling Price",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
        filterVariant: "range",
      },
      {
        accessorKey: "paymentProviderRef",
        header: "Reference",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <span>{renderedCellValue}</span>
          ) : (
            <div>&#8210;</div>
          ),
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: true,
    enableColumnFilters: true,
    enableDensityToggle: false,
    enableMultiSort: true,
    paginationDisplayMode: "pages",
    state: { pagination, isLoading, sorting },
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    rowCount: totalRowCount,
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
      withBorder: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <Button
          color="lightblue"
          onClick={handleDownloadPaymentReconciliation}
          leftIcon={<IconDownload />}
          variant="filled"
        >
          Export CSV
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <div>
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};

export default PaymentReconciliation;
